import type { Ref } from 'vue'

import type { Result } from '../utils/monad'

import { ofetch } from 'ofetch'
import { ref } from 'vue'
import { toast } from 'vue-sonner'

import { createClient } from '../client/client/client.gen'
import { useRegionStore } from '../stores/region'
import { useUserStore } from '../stores/user'
import { ApiError } from '../types/error'
import { errorToMessage } from '../utils/error'
import { Err, Ok } from '../utils/monad'

// Placeholder types for now - will be replaced by generated code
export interface V1ErrorObject {
  detail: string
  status?: number
  source?: {
    pointer?: string
    header?: string
    parameter?: string
  }
}

export type Options = Record<string, any>

// Create a function to get the API client with current region
function getApiClient() {
  const regionStore = useRegionStore()
  return createClient({
    baseUrl: regionStore.currentRegion.apiBaseUrl,
    fetch: ofetch.create({}).native,
  })
}

export interface ApiResponse<T> {
  data: T
  isLoading: Ref<boolean>
}

export interface PaginationOptions {
  pageSize?: number
  pageToken?: string
}

type DeepRequired<T> = {
  [P in keyof T]-?: T[P] extends object ? DeepRequired<T[P]> : T[P];
}

export async function doRequest<
  TApiResponse,
  TOptions extends Options,
  TData = DeepRequired<NonNullable<TApiResponse extends { data: infer U } ? U : never>>,
>(
  apiFn: (params: TOptions) => Promise<TApiResponse>,
  params?: TOptions,
  options: {
    pagination?: PaginationOptions
  } = {},
): Promise<Result<ApiResponse<TData>>> {
  const isLoading = ref(true)
  const client = getApiClient()

  // Add request interceptor for authentication
  client.interceptors.request.use((request) => {
    const { storageToken } = useUserStore()
    const token = storageToken
    if (token) {
      request.headers.set('Authorization', `Bearer ${token}`)
    }
    return request
  })

  try {
    // 处理分页参数 - 只在明确指定了 pagination 选项时才添加
    let processedOptions: TOptions = {
      ...params,
    } as TOptions

    // 只有当明确提供了 pagination 选项时，才添加分页参数
    if (options?.pagination) {
      const paginationParams: Record<string, string | number> = {}

      if (options.pagination.pageSize !== undefined) {
        paginationParams['pagination.pageSize'] = options.pagination.pageSize
      }

      if (options.pagination.pageToken) {
        paginationParams['pagination.pageToken'] = options.pagination.pageToken
      }

      // 合并查询参数和分页参数
      processedOptions = {
        ...processedOptions,
        query: {
          ...paginationParams,
          ...(params?.query as object),
        },
      } as TOptions
    }
    else if (params?.query) {
      // 如果没有分页选项但已有查询参数，保留原有查询参数
      processedOptions = {
        ...processedOptions,
        query: params.query,
      } as TOptions
    }

    // 处理请求体，移除空字符串
    if (processedOptions.body) {
      const body = Object.fromEntries(
        Object.entries(processedOptions.body).filter(([_, value]) => value !== ''),
      )
      processedOptions = { ...processedOptions, body } as TOptions
    }

    const apiResponse = await apiFn({ ...processedOptions, client } as TOptions)

    const { data, error, response: rawResponse } = apiResponse as { data: TData, error: { errors: V1ErrorObject[] }, response: Response }

    if (data && typeof data === 'object' && 'errors' in data) {
      const error = (data as { errors: V1ErrorObject[] }).errors[0]

      if (error.status === 401) {
        const userStore = useUserStore()
        await userStore.logout()
        return Err({ data: new ApiError(error), isLoading })
      }

      if (error.status === 403) {
        toast.error('权限不足')
        return Err({ data: new ApiError(error), isLoading })
      }

      throw new ApiError(error)
    }

    if (!rawResponse.ok || error) {
      // Error will be handled by expect() or returned to caller
      throw new ApiError(error.errors[0])
    }

    if (data == null) {
      throw new Error('Data is null')
    }

    return Ok({ data, isLoading })
  }
  catch (error) {
    toast.error(errorToMessage(error) || '操作失败')
    return Err({ data: error, isLoading })
  }
  finally {
    isLoading.value = false
  }
}

export const useApi = doRequest
